import { AppointmentType } from "~/api/API";
import { DurationMinutes } from "./common/types";

export const RECORDS_PREFIX = "QME";

// Routing test/prod
export const TOGGLEABLE_ROUTES = [
  "/appointments/new",
  "/appointments",
  "/active-visits",
  "/active-cases",
  "/past-cases",
  "/upcoming-appointments",
  "/bookings", // Alias for /upcoming-appointments for MA/Admin
  "/in-person-evaluations",
  "/billing-and-collection",
  "/today-visits",
];

// Scheduling
export const DEFAULT_DURATION = DurationMinutes["60_MINUTES"];

// Federal holidays that doctors typically work on and should not be shown as holidays in the scheduler
// These names must match exactly what the Nager.At API returns (https://date.nager.at/api/v3/publicholidays)
// Note: Non-federal holidays (Columbus Day, Indigenous Peoples' Day, etc.) are already filtered out
// by the global: true check in fetchHolidays()
export const EXCLUDED_HOLIDAYS = ["Veterans Day"] as const;

// Additional holidays to include even if they're not federal (global: false in API)
// These names must match exactly what the Nager.At API returns
export const ADDITIONAL_HOLIDAYS = ["Indigenous Peoples' Day"] as const;

// Holidays to derive from existing API holidays (e.g., day after Thanksgiving)
// baseHoliday: the holiday name to find in the API response
// offsetDays: number of days to add to the base holiday date
export const DERIVED_HOLIDAYS = [
  {
    name: "Day After Thanksgiving",
    localName: "Day After Thanksgiving",
    baseHoliday: "Thanksgiving Day",
    offsetDays: 1,
  },
] as const;

// Briefing
export const IFRAME_FILE_SHARING_DURATION_SECONDS = 3600 * 24;
export const LINK_REFRESH_INTERVAL_MS =
  IFRAME_FILE_SHARING_DURATION_SECONDS * 1000;

// TODO: encapsulate it to the specific StateSelect
export const STATE_OPTIONS = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

// Emails
export const INDIGO_SCHEDULING_EMAIL_ADDRESS_PROD = "schedule@getindigo.io";
export const INDIGO_SCHEDULING_EMAIL_ADDRESS_DEV = "schedule-dev@getindigo.io";
export const INDIGO_SCHEDULING_EMAIL_ADDRESS_STAGING =
  "schedule-stage@getindigo.io";
export const VISIT_FORM_BASE_URL = "https://form.jotform.com/252107586585061";
export const VISIT_FORM_BASE_URL_SPANISH =
  "https://form.jotform.com/253157218088158";

// Default values
export const DEFAULT_DATE_ISO = "1970-01-01";
export const DEFAULT_TIME_ISO = "00:00:00";
export const DEFAULT_YEAR_MONTH = "1970-01";
export const DEFAULT_APPOINTMENT_GLOBAL_PARTITION_KEY = "APPOINTMENT";

// GraphQL
export const MAX_LIST_QUERY_LIMIT = 300;

// File extensions
export const AUDIO_FILE_EXTENSIONS = [
  ".mp3",
  ".wav",
  ".m4a",
  ".mp4",
  ".flac",
  ".ogg",
  ".amr",
  ".webm",
];

// Appointments constants

export const APPOINTMENT_TYPES_WITH_REPORTS: readonly AppointmentType[] = [
  AppointmentType.QME,
  AppointmentType.QME_REEVALUATION,
  AppointmentType.AME,
  AppointmentType.AME_REEVALUATION,
  AppointmentType.SUPPLEMENTAL,
  AppointmentType.SIBTF,
  AppointmentType.SIBTF_REEVALUATION,
  AppointmentType.IME,
  AppointmentType.IME_REEVALUATION,
  AppointmentType.IME_ADR,
  AppointmentType.IME_ADR_REEVALUATION,
];

export const appointmentTypeMap: Record<AppointmentType, string> = {
  [AppointmentType.QME]: "QME",
  [AppointmentType.QME_REEVALUATION]: "QME Re-eval",
  [AppointmentType.AME]: "AME",
  [AppointmentType.AME_REEVALUATION]: "AME Re-eval",
  [AppointmentType.SUPPLEMENTAL]: "Supplemental",
  [AppointmentType.DEPOSITION]: "Deposition",
  [AppointmentType.BRIEFING]: "Briefing",
  [AppointmentType.SIBTF]: "SIBTF",
  [AppointmentType.SIBTF_REEVALUATION]: "SIBTF Re-eval",
  [AppointmentType.IME]: "IME",
  [AppointmentType.IME_REEVALUATION]: "IME Re-eval",
  [AppointmentType.IME_ADR]: "IME ADR",
  [AppointmentType.IME_ADR_REEVALUATION]: "IME ADR Re-eval",
  [AppointmentType.RECORD_REVIEW]: "Record Review",
};

/**
 * Indigo color palette, matching Tailwind config.
 */
export const COLORS = {
  primary: {
    100: "#F8F8FE",
    150: "#DDDAFB80",
    200: "#DDDAFB",
    300: "#BCB6F8",
    500: "#9292E8",
    700: "#4E44CB",
    800: "#252086",
    900: "#0F0C59",
  },
  gray: {
    25: "#FAFAFA",
    50: "#F0F1F3",
    300: "#B7B7B7",
    500: "#7C7C7C",
    700: "#4A4A4A",
    900: "#333333",
  },
  green: {
    100: "#E0F2E2",
    300: "#8DD8B8",
    500: "#40BE88",
    600: "#33996D",
    900: "#0C5B58",
  },
  red: {
    50: "#FBEAEA",
    300: "#E88987",
    500: "#D83835",
    600: "#B32523",
  },
  danger: {
    50: "#FFCAD44D",
    200: "#B2181580",
    500: "#D83835",
  },
};
